import { Blockchain } from '@repo/prsn-blockchain';
import {
  Chain,
  createPublicClient,
  fallback,
  http,
  PublicClient,
  Transport,
} from 'viem';
import { base, baseSepolia, mainnet, sepolia } from 'viem/chains';

import { env } from '@web/utils/env';

export const chains: Record<Blockchain, Chain> =
  process.env.NEXT_PUBLIC_ENVIRONMENT === 'release'
    ? {
        ethereum: mainnet,
        base,
      }
    : {
        ethereum: sepolia,
        base: baseSepolia,
      };

// Read transport: 5-deep HTTP fallback chain per chain. viem tries each
// provider in order on every request; if one fails or times out, it
// transparently falls through to the next.
//
// Primary (index 0) is our Ankr free-tier account key — the only provider
// with a documented rate limit (1,800 req/min). Indices 1-4 are best-effort
// unmetered providers from different operators, chosen so a single-provider
// outage (Cloudflare incident, Ankr maintenance, etc.) never breaks the app.
// Endpoint reliability was probed end-to-end on 2026-05-14; see CLAUDE.md
// "RPC providers & fallback strategy" for the full rationale, the rejected
// endpoints (llamarpc, cloudflare-eth, blockpi — broken or too unreliable),
// and the premium-Ankr rollback procedure.
//
// HTTP only. We deliberately don't use WebSocket transport — viem's
// `fallback()` eagerly opens every transport's socket on construction, and
// free WS endpoints reject anonymous traffic, spam-looping connection errors
// in the console. We have no live-subscription features anywhere in the
// web client; HTTP polling covers everything.
//
// We also deliberately do NOT use `unstable_connector(injected)`: it talks to
// the wallet's injected provider at config-init time, which hangs when
// multiple wallet extensions race to override window.ethereum.
//
// `env.ethereum.RPC_WEBSOCKET` / `env.base.RPC_WEBSOCKET` now hold the
// keyed Ankr HTTPS URL (the env key name is historical — the values are
// HTTPS, not WSS). Kept under the old key for backwards compat with CI
// pipeline + env validator.
export const transports: Record<Blockchain, Transport> = {
  ethereum: fallback([
    http(env.ethereum.RPC_WEBSOCKET), // Ankr free key, 1,800 req/min documented
    http('https://ethereum-rpc.publicnode.com'), // Allnodes, unmetered best-effort
    http('https://rpc.flashbots.net'), // Flashbots
    http('https://eth.drpc.org'), // dRPC anonymous
    http('https://eth-mainnet.public.blastapi.io'), // Bware Labs Blast public
  ]),
  base: fallback([
    http(env.base.RPC_WEBSOCKET), // Ankr free key (same key for both chains)
    http('https://base-rpc.publicnode.com'), // Allnodes
    http('https://mainnet.base.org'), // Coinbase official Base endpoint
    http('https://base.drpc.org'), // dRPC
    http('https://1rpc.io/base'), // 1RPC
  ]),
};

export const clients: Record<Blockchain, PublicClient> = {
  ethereum: createPublicClient({
    chain: chains.ethereum,
    transport: transports.ethereum,
  }),
  base: createPublicClient({ chain: chains.base, transport: transports.base }),
};
